import cx from 'classnames';
import {map, filter} from 'lodash';
import {useState, useEffect, useCallback, Fragment, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {ConfirmModal, Icon, RequiredMark} from 'components';
import {useUserSession, usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';

import {ScreenshotPreview} from '../ScreenshotPreview';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.VERIFICATION_SCREENSHOTS_SELECTION_MODAL;

type Props = {
    open: boolean;
    sections: {
        label: string;
        key: string;
        required?: boolean;
    }[];
    onClose: (confirmed: boolean, data?: any) => void;
};

export const ScreenshotsSelectionModal: React.FC<Props> = ({open, sections, onClose}: Props) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);
    const {receivedScreenshots, isScreenshotsPreviewEnabled} = useUserSession();
    const [isPreview, setIsPreview] = useState(false);
    const [sectionScreenshots, setSectionScreenshots] = useState({});

    const hasReceivedScreenshots = useMemo(() => receivedScreenshots.length > 0, [receivedScreenshots]);

    const hasRequiredScreenshots = useMemo(() =>
        sections.every((section) => !section.required || !!sectionScreenshots[section.key]
        ), [sections, sectionScreenshots]);

    const togglePreview = useCallback(() => {
        setIsPreview((prev) => !prev);
    }, []);

    const selectSectionScreenshot = useCallback((sectionKey: string, screenshotName: string) => {
        setSectionScreenshots((prev) => ({...prev, [sectionKey]: screenshotName}));
    }, []);

    const renderScreenshotsPreviews = useCallback((sectionKey: string) => (
        isScreenshotsPreviewEnabled &&
            <div className={cx('tw-flex tw-overflow-x-auto', {'tw-hidden': !isPreview})}>
                {map(receivedScreenshots, (screenshot, index) => {
                    const selected = sectionScreenshots[sectionKey] === screenshot.name;
                    return (
                        <div
                            key={index}
                            className={cx('tw-w-20 tw-flex tw-flex-col tw-items-center tw-mr-4 last:tw-mr-0', {'tw-opacity-30': !selected})}
                        >
                            <div
                                className='tw-w-20 tw-h-20 tw-border tw-border-default tw-flex-center tw-cursor-pointer tw-relative'
                                onClick={() => selectSectionScreenshot(sectionKey, selected ? null : screenshot.name)}
                            >
                                <ScreenshotPreview screenshot={screenshot} hidden={!isPreview} />
                                {selected &&
                                    <div className='tw-absolute tw-w-full tw-h-full tw-flex-center'>
                                        <Icon name='check-circle' color='success' size='xl' fill='white' />
                                    </div>}
                            </div>
                            <span className='tw-break-words'>
                                {screenshot.name}
                            </span>
                        </div>
                    );
                })}
            </div>
    ), [receivedScreenshots, isPreview, sectionScreenshots, isScreenshotsPreviewEnabled, selectSectionScreenshot]);

    const renderScreenshotsList = useCallback((sectionKey: string) => (
        !isPreview &&
            <div>
                {map(receivedScreenshots, (screenshot, index) => {
                    const selected = sectionScreenshots[sectionKey] === screenshot.name;
                    return (
                        <div
                            key={index}
                            className={cx('tw-flex-center tw-my-1 tw-px-2 tw-py-1 hover:tw-bg-default tw-cursor-pointer',
                                {'tw-opacity-50': !selected})}
                            onClick={() => selectSectionScreenshot(sectionKey, selected ? null : screenshot.name)}
                        >
                            <div className={cx('tw-flex-1', {'tw-pr-2': selected})}>{screenshot.name}</div>
                            {selected && <Icon name='check-circle' color='success' size='sm' />}
                        </div>
                    );
                })}
            </div>
    ), [receivedScreenshots, isPreview, sectionScreenshots, selectSectionScreenshot]);

    useEffect(() => {
        if (open && !prevOpen) {
            const result = filter(sections, (section) => section.required).reduce((acc, section, index) => ({
                ...acc,
                [section.key]: receivedScreenshots[(receivedScreenshots.length + index) % receivedScreenshots.length]?.name
            }), {});
            setSectionScreenshots(result);
        }
    }, [open, prevOpen, sections, receivedScreenshots]);

    return (
        <ConfirmModal
            open={open}
            title={
                <div className='tw-flex tw-items-center'>
                    <div className='tw-flex-1'>{t(T_KEYS.TITLE)}</div>
                    {isScreenshotsPreviewEnabled && hasReceivedScreenshots &&
                        <Icon name={isPreview ? 'eye-off' : 'eye'} button onClick={togglePreview} />}
                </div>
            }
            fullWidth
            maxWidth='sm'
            okDisabled={!hasReceivedScreenshots || !hasRequiredScreenshots}
            data={sectionScreenshots}
            onClose={onClose}
        >
            {map(sections, (section) => (
                <div key={section.key} className='tw-mb-5 last:tw-mb-0'>
                    <div className='tw-mb-1'>
                        {section.label}
                        {section.required && <RequiredMark />}
                    </div>
                    {hasReceivedScreenshots ?
                        <Fragment>
                            {renderScreenshotsPreviews(section.key)}
                            {renderScreenshotsList(section.key)}
                        </Fragment> :
                        <div className='tw-text-default-dimmed tw-mb-4'>{t(T_KEYS.NO_SCREENSHOTS)}</div>}
                </div>
            ))}
        </ConfirmModal>);
};
